import { Box, DialogTitle, styled } from '@mui/material';
import { InfoCircle, Plus } from '@styled-icons/bootstrap';
import ComponentChainTable from 'FeatureModules/ComponentChain/Components/Table/ComponentChainTable';
import { ECOMPONENT_CHAIN_COLUMN_IDS } from 'FeatureModules/ComponentChain/utils/headerColumns';
import useComponentChains from 'FeatureModules/ComponentChain/hooks/useComponentChains';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import { StyledDialogContent } from 'FeatureModules/ProductPage/Components/Create/styles';
import { ThemeButton, ThemeTypography } from 'DesignSystem';
import { StyledDialog } from 'DesignSystem/Overlays/Dialog/DialogDefault/DialogDefault';
import React, { FC, useMemo, useState } from 'react';
import { IChainMapping } from 'FeatureModules/ComponentChain/types/chain.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import { getChainImage } from 'FeatureModules/ComponentChain/utils/chainTitle.utils';
import EmptyState from 'Components/EmptyState';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import PageSubTitle from 'Components/PageSubTitle';
import ScrollableTableContainer from 'Components/ScrollableTableContainer';

interface IImportComponentChainsDialogProps {
  // Used to exclude the current chain from the list of available chains and show title and image of the current chain
  chain: Pick<IChainMapping, 'id' | 'title' | 'image' | 'subChains' | 'productId'>;
  onImport: (selectedIds: string[]) => void;
}
const ChainImage = styled('div')<{ backgroundImage: string }>(({ backgroundImage }) => ({
  width: 32,
  height: 32,
  borderRadius: 4,
  flexShrink: 0,
  backgroundImage: `url(${backgroundImage})`,
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  marginRight: 8,
}));

const DialogTitleStyled = styled(DialogTitle)(() => ({
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingRight: 40,
}));

const Dialog = styled(StyledDialog)(({ theme }) => ({
  '& .MuiDialog-paperWidthXl': {
    width: 1300,
    minHeight: 500,
  },
}));

const StyledActions = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  padding: theme.spacing(0, 4, 4),
}));

const ImportComponentChainsDialog: FC<IDefaultDialogProps & IImportComponentChainsDialogProps> = ({
  open,
  chain,
  onImport,
  onClose,
}) => {
  const importIntoChainType = chain.productId ? 'SUPPLY_CHAIN' : 'COMPONENT_CHAIN';
  const {
    componentChains,
    loading,
    error,
    searchTerm,
    hasNextPage,
    setSearchTerm,
    handlePageEndReached,
  } = useComponentChains({
    skipUseQueryParamSearch: true,
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleSubmit = async () => {
    onImport(selectedIds);
    onClose?.();
  };

  const handleRowClick = (id: string) => {
    const isItemSelected = selectedIds?.includes(id);
    if (!isItemSelected) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(ids => ids.filter(selectedChainId => selectedChainId !== id));
    }
  };

  const visibleComponentChains = useMemo(() => {
    // Exclude the current chain and all already imported chains from the list importable chains
    const excludeComponentChainIds = [
      chain.id,
      ...chain.subChains.map(({ childChainId }) => childChainId),
    ];
    return componentChains?.filter(({ id }) => !excludeComponentChainIds.includes(id));
  }, [componentChains, chain]);

  if (error) {
    return <ErrorState />;
  }

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="xl" data-testid="import-chain-dialog">
      <DialogTitleStyled>
        <Box display="flex" alignItems="center">
          <Box mr={1}>
            <Plus size={16} />
          </Box>
          <PageSubTitle title="Import Component Chains" />
        </Box>
      </DialogTitleStyled>
      <StyledDialogContent>
        <Box display="flex" alignItems="center" mt={-1} mb={2}>
          <InfoCircle size={16} />
          <Box mr={1} />
          <ThemeTypography variant="BODY_MEDIUM">
            {importIntoChainType === 'COMPONENT_CHAIN'
              ? 'Select sub component chains that should be part of this component chain:'
              : 'Select all component chains that are part of this supply chain:'}
          </ThemeTypography>
          <FlexBox ml={1}>
            <ChainImage backgroundImage={getChainImage(chain)} />
            <ThemeTypography variant="BODY_MEDIUM_BOLD">{chain.title}</ThemeTypography>
          </FlexBox>
        </Box>
        {(!!componentChains.length || searchTerm?.length !== 0) && (
          <Box mt={3} mb={0.5}>
            <SearchToolbar
              setSearchTerm={setSearchTerm}
              searchTerm={searchTerm || ''}
              placeholder="Search"
              className="search-input"
              data-testid="search-input-import-chain"
            />
          </Box>
        )}
        {!visibleComponentChains.length && !loading && (
          <EmptyState
            message="You have not created any component chains or all available component chains are already selected."
            data-testid="empty-state-import-chain"
          />
        )}
        {loading ? (
          <Loader />
        ) : (
          <ScrollableTableContainer maxHeight="calc(100vh - 300px)" pb={1}>
            <ComponentChainTable
              displayTableHeader={visibleComponentChains.length > 0}
              columnKeys={[
                ECOMPONENT_CHAIN_COLUMN_IDS.SELECTION,
                ECOMPONENT_CHAIN_COLUMN_IDS.TITLE,
                ECOMPONENT_CHAIN_COLUMN_IDS.OWNER,
                ECOMPONENT_CHAIN_COLUMN_IDS.INVOLVED_PARTNERS,
              ]}
              hasNextPage={hasNextPage}
              componentChains={visibleComponentChains}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              onRowClick={handleRowClick}
              onPageEndReached={handlePageEndReached}
            />
          </ScrollableTableContainer>
        )}
      </StyledDialogContent>
      <StyledActions>
        <ThemeButton onClick={onClose} color="BLUE_ICE" size="large">
          Cancel
        </ThemeButton>
        <Box ml={2}>
          <ThemeButton
            color="PRIMARY"
            size="large"
            onClick={handleSubmit}
            data-testid="submit-import-chain-btn"
          >
            Import selected
          </ThemeButton>
        </Box>
      </StyledActions>
    </Dialog>
  );
};

export default ImportComponentChainsDialog;
